import { ref, computed, watch, onMounted, unref } from 'vue';
import type { Ref } from 'vue';
import { useNotify } from '@/composables';
import yardSpotAreaService from '@satellite/services/yardSpotArea-service';

type Area = {
  id?: string;
  name?: string;
};

export interface UseYardAreaPresenterOptions {
  areaId?: Ref<string | null | undefined>;
  area?: Ref<Area | null | undefined>;
}

export function useYardAreaPresenter(
  { areaId, area }: UseYardAreaPresenterOptions = {},
  lazy = false
) {
  const notify = useNotify();

  const fetchedArea = ref(null);
  const isLoading = ref(false);

  const currentArea = computed(() => {
    return {
      ...area?.value,
      ...fetchedArea.value
    };
  });

  const areaName = computed(() => {
    if (!currentArea.value) {
      return null;
    }

    return currentArea.value.name || null;
  });

  const fetchArea = async id => {
    if (!id) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await yardSpotAreaService.getOneById(id);
      fetchedArea.value = response;
    } catch (error) {
      notify('Error fetching area', 'error');
      console.error('Error fetching area:', error);
      fetchedArea.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = () => {
    fetchedArea.value = null;

    const currentAreaIdValue = unref(areaId);
    const currentAreaValue = unref(area);
    const id = currentAreaIdValue || currentAreaValue?.id;

    if (id) {
      fetchArea(id);
    }
  };

  watch([() => unref(areaId), () => unref(area)?.id], () => refresh());

  onMounted(() => {
    if (!lazy) {
      refresh();
    }
  });

  return {
    isLoading,
    areaName,

    fetchArea,
    refresh
  };
}
